import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import {
  getFusionResultLabel,
  getFusionResultTone,
} from "lib/utils/fusionResult";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/templates/ud1000-template.xlsx`;

const TONE_FILL_BY_RESULT = {
  normal: {
    title: "d9d9d9",
    label: "f2f2f2",
  },
  warning: {
    title: "d8bee4",
    label: "ebdef1",
  },
  abnormal: {
    title: "ffceb0",
    label: "ffe7d8",
  },
  recheck: {
    title: "cbdd61",
    label: "e3eca6",
  },
};

const TONE_LABEL_CELLS = [
  "A3",
  "A4",
  "A5",
  "A6",
  "A7",
  "A8",
  "A12",
  "C3",
  "C4",
  "C5",
  "C6",
  "E4",
  "E5",
  "E6",
  "E8",
  "E10",
  "E12",
  "G3",
  "G4",
  "G5",
  "G6",
];

function setCellValue(sheet, address, value) {
  sheet.getCell(address).value = value ?? "";
}

function getThermalSummary(form, t) {
  const clickPoints = Array.isArray(form?.click_points) ? form.click_points : [];

  if (clickPoints.length > 0) {
    return clickPoints
      .map((point, index) => `L${index + 1}: ${point?.value ?? ""}`)
      .join("\n");
  }

  return `${t("imageFileIndex.report.thermal.max")}: ${
    form?.thermal_max ?? ""
  }\n${t("imageFileIndex.report.thermal.min")}: ${
    form?.thermal_min ?? ""
  }\n${t("imageFileIndex.report.thermal.center")}: ${
    form?.thermal_center ?? ""
  }`;
}

function mergeCellAlignment(sheet, address, alignment) {
  const cell = sheet.getCell(address);
  cell.alignment = {
    ...(cell.alignment ?? {}),
    ...alignment,
  };
}

function mergeCellFont(sheet, address, font) {
  const cell = sheet.getCell(address);
  cell.font = {
    ...(cell.font ?? {}),
    ...font,
  };
}

function setCellFill(sheet, address, fill) {
  sheet.getCell(address).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: fill },
  };
}

function addBase64Image(workbook, sheet, base64, range) {
  if (!base64 || typeof base64 !== "string") return;

  const imageId = workbook.addImage({
    base64,
    extension: "png",
  });

  sheet.addImage(imageId, range);
}

export async function loadUdExcelTemplateWorkbook() {
  const response = await fetch(TEMPLATE_URL);

  if (!response.ok) {
    throw new Error(`template fetch failed: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  return workbook;
}

export function populateUdImageExcelWorksheet({
  workbook,
  sheet,
  form,
  images,
  t,
  i18n,
}) {
  if (!workbook || !sheet) {
    throw new Error("populateUdImageExcelWorksheet requires workbook and sheet");
  }

  sheet.views = [
    {
      state: "normal",
      style: "pageBreakPreview",
      activeCell: "A1",
      zoomScale: 100,
      zoomScaleNormal: 100,
    },
  ];
  const fusionTone =
    getFusionResultTone(form?.fusion_result_code ?? form?.fusion_result) ??
    "normal";
  const toneFill =
    TONE_FILL_BY_RESULT[fusionTone] ?? TONE_FILL_BY_RESULT.normal;
  const fusionResultLabel = getFusionResultLabel(
    form?.fusion_result_code ?? form?.fusion_result,
    i18n,
    {
      variant: "image",
      fallback: form?.fusion_result ?? "",
    },
  );
  const thermalHeader = t("imageFileIndex.report.temperature");
  const thermalPointSummary = getThermalSummary(form, t);

  TONE_LABEL_CELLS.forEach((address) => {
    setCellFill(sheet, address, toneFill.label);
    mergeCellFont(sheet, address, { bold: true, size: 9 });
  });

  setCellFill(sheet, "A1", toneFill.title);
  mergeCellFont(sheet, "A1", { bold: true, size: 17 });
  mergeCellFont(sheet, "D5", { size: 8 });
  mergeCellAlignment(sheet, "C5", {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  });
  mergeCellAlignment(sheet, "D5", {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  });
  mergeCellFont(sheet, "B7", { size: 9 });
  mergeCellAlignment(sheet, "B7", {
    horizontal: "left",
    vertical: "middle",
    wrapText: true,
  });

  setCellValue(sheet, "A1", t("imageFileIndex.report.title"));
  setCellValue(sheet, "A2", "");
  setCellValue(sheet, "A3", t("imageFileIndex.report.facilityType"));
  setCellValue(sheet, "A4", t("imageFileIndex.report.buildingName"));
  setCellValue(sheet, "A5", `${t("imageFileIndex.report.decibel")}\n(dB)`);
  setCellValue(sheet, "A6", `${t("imageFileIndex.report.distance")}\n(m)`);
  setCellValue(
    sheet,
    "A7",
    `${t("imageFileIndex.report.inspectionResult.title")}\n${t("imageFileIndex.report.inspectionResult.label")}`,
  );
  setCellValue(sheet, "A8", t("imageFileIndex.report.ultrasonicDiagnosis"));
  setCellValue(sheet, "A9", "Ultrasonic Image");
  setCellValue(sheet, "A12", t("imageFileIndex.report.thermalDiagnosis"));
  setCellValue(sheet, "A13", "Thermal Image");

  setCellValue(sheet, "B3", form?.facility_type);
  setCellValue(sheet, "B4", form?.building_name);
  setCellValue(sheet, "B5", form?.avg_decibel);
  setCellValue(sheet, "B6", form?.distance);
  setCellValue(
    sheet,
    "B7",
    typeof form?.inspection_result === "string"
      ? form.inspection_result.replace(/^\n+/, "")
      : form?.inspection_result,
  );

  setCellValue(sheet, "C3", t("imageFileIndex.report.partName"));
  setCellValue(sheet, "C4", t("imageFileIndex.report.floor"));
  setCellValue(sheet, "C5", `${thermalHeader}\n(℃)`);
  setCellValue(
    sheet,
    "C6",
    `${t("imageFileIndex.report.ambientTemperature")}\n(°C)`,
  );

  setCellValue(sheet, "D3", form?.facility_name);
  setCellValue(sheet, "D4", form?.floor);
  setCellValue(sheet, "D5", thermalPointSummary);
  setCellValue(sheet, "D6", form?.ambient_temp);

  setCellValue(sheet, "E4", t("imageFileIndex.report.location"));
  setCellValue(sheet, "E5", t("imageFileIndex.report.uvDetected"));
  setCellValue(sheet, "E6", t("imageFileIndex.report.equipmentUsed"));
  setCellValue(sheet, "E8", t("imageFileIndex.report.fastFourierTransform"));
  setCellValue(sheet, "E9", "");
  setCellValue(sheet, "E10", "SPECTROGRAM");
  setCellValue(sheet, "E11", "Spectrogram Image");
  setCellValue(sheet, "E12", t("imageFileIndex.report.ultravioletDiagnosis"));
  setCellValue(sheet, "E13", "UV Image");

  setCellValue(sheet, "F4", form?.location);
  setCellValue(sheet, "F5", form?.uv_detected);
  setCellValue(sheet, "F6", form?.equipment_used);

  setCellValue(sheet, "G3", t("imageFileIndex.report.partName"));
  setCellValue(sheet, "G4", t("imageFileIndex.report.shootingDate"));
  setCellValue(sheet, "G5", t("imageFileIndex.report.fusionResultMultiline"));
  setCellValue(sheet, "G6", t("imageFileIndex.report.serialNumber"));

  setCellValue(sheet, "H3", form?.part_name);
  setCellValue(sheet, "H4", form?.shooting_date);
  setCellValue(sheet, "H5", fusionResultLabel);
  setCellValue(sheet, "H6", form?.serial_number);

  addBase64Image(workbook, sheet, images?.image_visual, "A9:D11");
  addBase64Image(workbook, sheet, images?.markup_image_graph, "E9:H9");
  addBase64Image(workbook, sheet, images?.image_spectrogram, "E11:H11");
  addBase64Image(
    workbook,
    sheet,
    images?.markup_image_triplespot || images?.markup_image_thermal,
    "A13:D13",
  );
  addBase64Image(workbook, sheet, images?.image_uv, "E13:H13");
}

export async function downloadUdExcelReport({ form, images, t, i18n }) {
  const workbook = await loadUdExcelTemplateWorkbook();
  const sheet = workbook.worksheets[0];
  populateUdImageExcelWorksheet({
    workbook,
    sheet,
    form,
    images,
    t,
    i18n,
  });

  const fileName =
    form?.file_clean_file_name ||
    form?.clean_file_name ||
    form?.upload_id ||
    "diagnosis_report";

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
}

export default function ExcelDownloadBtn({ images, form, t }) {
  const { i18n } = useTranslation();

  const handleDownload = async () => {
    try {
      await downloadUdExcelReport({ form, images, t, i18n });
    } catch (error) {
      console.error("[UdExcelDownloadBtn] download failed", error);
    }
  };

  return (
    <button className="btn-bg-blue" onClick={handleDownload}>
      {t("imageFileIndex.buttons.ExcelDownload")}
    </button>
  );
}
